import { useQuery } from '@tanstack/react-query';
import { useUniversalNavContext } from '../../universal-nav/hooks/useUniversalNavContext';

import type { ZapierAccountUsage } from '../types';

type ZapierAccountUsageRaw = {
  id: number;
  zap_metrics: {
    total_tasks: number;
    hours_saved: number;
    zap_off_count: number;
    zap_on_count: number;
    zap_draft_cont: number;
    zap_total_count: number;
  };
  limit: {
    action_limit: number;
    zap_limit: number;
  };
  name: string;
  period_end: string;
  period_start: string;
  period_reset: string;
};

// Refer to graphql-proxy resolver for the mapping below:
// https://gitlab.com/zapier/zapier/-/blob/main/graphql-proxy/src/structures/Account/Usage.js
export const prepareAccountUsageData = (
  rawData: ZapierAccountUsageRaw
): ZapierAccountUsage => {
  return {
    id: rawData.id,
    periodEnd: rawData.period_end,
    tasks: {
      limit: rawData.limit.action_limit,
      total: rawData.zap_metrics.total_tasks,
    },
    zaps: {
      limit: rawData.limit.zap_limit,
      on: rawData.zap_metrics.zap_on_count,
    },
  };
};

export const useQueryAccountUsage = (currentAccountId?: number) => {
  const { apiHostname } = useUniversalNavContext();

  const result = useQuery({
    queryKey: ['accountUsage', currentAccountId],
    queryFn: async () => {
      if (!currentAccountId)
        throw new Error('currentAccountId is not available');

      const response = await fetch(
        `${apiHostname}/api/v4/accounts/${encodeURIComponent(
          currentAccountId
        )}/usage`,
        {
          credentials:
            process.env.NODE_ENV !== 'test' ? 'include' : 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      return await response.json();
    },
    select: prepareAccountUsageData,
    enabled: !!currentAccountId,
  });

  return {
    ...result,
    // isInitialLoading returns false if the query is disabled.
    // isLoading returns true, which is undesired behavior.
    isLoading: result.isInitialLoading,
  } as typeof result;
};
